import AccountDetailsForm from 'components/wizard/account-details-form'
import {
  Box,
  Button,
  Grid,
  Typography,
  Step,
  Stepper,
  StepLabel,
  StepContent,
  StepConnector,
  useMediaQuery,
} from '@mui/material'
import CropVarieties from 'components/wizard/crop-varieties'
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material'
import EquipmentsComponent from 'components/farm-settings/equipment'
import CropHeadersComponent from 'components/farm-settings/headers'
import { editTenant, editTenantPreferences } from 'api/tenant'
import { editUserAccount } from 'api/user'
import { createFarm } from 'api/farm'
import {
  closeModal,
  shouldShowDashboard,
  clearOnboardingTenantCreated,
  addEvent,
} from 'actions/app'
import { configureCropVarieties } from 'api/crop-variety'
import { updateCountryExtent } from 'actions/field'
import { createClient } from 'api/client'
import { useLocation } from 'react-router'
import { getCountryPreferenceValue } from 'common/tenant-country'
import colors from 'common/colors'

const stepperBgLandscape = `${WIZARD_ASSETS_BASE_URL}/stepper-landscape-bg.png`
const stepperBgPortrait = `${WIZARD_ASSETS_BASE_URL}/stepper-portrait-bg.png`
import { createDefaultClientAndFarm } from './create-default-client-farm'

// Rejects entries left behind by a different tenant on a shared browser (e.g. abandoned onboarding, account switch).
function getStoredWizardFarmName(tenantId) {
  const raw = localStorage.getItem('wizardFarmName')
  if (!raw) return undefined
  try {
    const stored = JSON.parse(raw)
    if (tenantId && stored?.tenantId === tenantId) {
      return stored.farmName
    }
  } catch (e) {
    // malformed entry, fall through to remove it
  }
  localStorage.removeItem('wizardFarmName')
  return undefined
}

function GetStepContent(props) {
  const tenantInfo = useCallback((data) => {
    props.tenantInfo(data)
  })

  const enableEqNextStep = useCallback((data) => {
    props.enableEqNextButton(data)
  })

  const enableHeadersNextStep = useCallback((data) => {
    props.enableHeadersNextButton(data)
  })

  const enableAccNextStep = useCallback((data) => {
    props.enableAccNextButton(data)
  })

  const enableFinish = useCallback((data) => {
    props.enableNextButton(data)
  })

  const selectedCrops = useCallback((data) => {
    props.selectedCrops(data)
  })

  const setEditTenant = useCallback((data) => {
    props.editTenant(data)
  })

  switch (props.activeStepParam) {
    case 0:
      return (
        <AccountDetailsForm
          enableAccButton={enableAccNextStep}
          formFields={tenantInfo}
          setEditTenant={setEditTenant}
        />
      )
    case 1:
      return (
        <EquipmentsComponent
          enableEqNextButton={enableEqNextStep}
          hideTimeDelay
        />
      )
    case 2:
      return (
        <CropHeadersComponent enableHeadersNextButton={enableHeadersNextStep} />
      )
    case 3:
      return (
        <CropVarieties
          enableFinish={enableFinish}
          selectedCrops={selectedCrops}
        />
      )
    default:
      return 'Unknown step'
  }
}

export default function WizardStepper({ partner }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const matches = useMediaQuery('(orientation: landscape)')

  const [activeStep, setActiveStep] = React.useState(0)
  const [tenantInputs, setTenantInputs] = useState()
  const [accNextBtnState, setAccNextBtnState] = useState(true)
  const [eqNextBtnState, setEqNextBtnState] = useState(true)
  const [headersNextBtnState, setHeadersNextBtnState] = useState(true)
  const [enableNextBtnState, setEnableNextBtnState] = useState(true)

  const [selectedCrops, setSelectedCrops] = useState([])
  const [isEditTenant, setIsEditTenant] = useState(false)

  const permissions = useSelector((state) => state.account.permissions)
  const [showPermissions, setShowPermissions] = useState({})
  const userDetails = useSelector((state) => state.account.user)

  const onboardingTenantCreated = useSelector(
    (state) => state.app.onboardingTenantCreated,
  )

  const { state } = useLocation()
  const { farmName: farmNameFromState } = state ?? {}
  const farmName =
    farmNameFromState || getStoredWizardFarmName(userDetails?.tenantId)

  useEffect(() => {
    setShowPermissions({
      skipWizard: permissions.includes('tenant:list'),
    })
  }, [permissions])

  const steps = [
    {
      label: t('account'),
    },
    {
      label: t('equipment'),
    },
    {
      label: t('headers'),
    },
    {
      label: t('crops'),
    },
  ]

  useEffect(() => {
    if (localStorage.getItem('wizardActiveStep')) {
      if (localStorage.getItem('wizardActiveStep') != 'null') {
        setActiveStep(parseInt(localStorage.getItem('wizardActiveStep')))
      }
    }
  }, [])

  const handleNext = () => {
    if (activeStep === 0) {
      if (farmName) {
        tenantInputs['displayName'] = farmName
      }
      const countryPreferenceValue = getCountryPreferenceValue(tenantInputs)
      const countryPreferenceRequest =
        isEditTenant &&
        countryPreferenceValue !== undefined &&
        countryPreferenceValue !== null &&
        countryPreferenceValue !== ''
          ? dispatch(
              editTenantPreferences({
                path: 'general.country',
                value: countryPreferenceValue,
              }),
            )
          : Promise.resolve()

      countryPreferenceRequest
        .then(() => dispatch(editTenant(tenantInputs)))
        .then(() => {
          dispatch(
            editUserAccount({
              email: tenantInputs.email,
              fullName: tenantInputs.fullName,
              phoneNumber: tenantInputs.phoneNumber,
              eusaDate: userDetails.eusaDate,
            }),
          )

          if (onboardingTenantCreated) {
            return createDefaultClientAndFarm({
              dispatch,
              tenantInputs,
              createClientAction: createClient,
              createFarmAction: createFarm,
              editTenantPreferencesAction: editTenantPreferences,
            }).then(() => {
              dispatch(clearOnboardingTenantCreated())
            })
          }
        })
        .then(() => {
          if (tenantInputs?.country) {
            dispatch(updateCountryExtent(tenantInputs.country.extent))
          }
          setActiveStep((prevActiveStep) => prevActiveStep + 1)
          // set current step
          localStorage.setItem('wizardActiveStep', activeStep)
        })
        .catch(() => {
          dispatch(
            addEvent(
              'Something went wrong while setting up your default client and farm. Please try again.',
              'Onboarding setup failure',
            ),
          )
        })
    } else if (activeStep === 3) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
      // set current step
      localStorage.setItem('wizardActiveStep', activeStep)
      // Clear any existing error modal due to missing data from a failure to fetch the tenant for example.
      dispatch(closeModal())
      dispatch(clearOnboardingTenantCreated())
      dispatch(configureCropVarieties({ crops: selectedCrops }))
      dispatch(shouldShowDashboard())
      localStorage.setItem('wizardActiveStep', null)
      localStorage.removeItem('wizardFarmName')
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
      // set current step
      localStorage.setItem('wizardActiveStep', activeStep)
    }
  }

  const handleSkipWizard = () => {
    dispatch(closeModal())
    dispatch(clearOnboardingTenantCreated())

    dispatch(shouldShowDashboard())
    localStorage.setItem('wizardActiveStep', null)
    localStorage.removeItem('wizardFarmName')
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const getTenantInfo = (data) => {
    setTenantInputs(data)
  }

  const getAccNextBtnState = (data) => {
    setAccNextBtnState(data)
  }

  const getEqNextBtnState = (data) => {
    setEqNextBtnState(data)
  }

  const getHeadersNextBtnState = (data) => {
    setHeadersNextBtnState(data)
  }

  const getEnableNextButton = (data) => {
    setEnableNextBtnState(data)
  }

  const getSelectedCrops = (data) => {
    setSelectedCrops(data)
  }

  const getIsEditTenant = (data) => {
    setIsEditTenant(data)
  }

  return (
    <Grid container>
      <Grid
        size={{ md: 1, lg: 4 }}
        sx={{
          backgroundImage: `url(${stepperBgLandscape})`,
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          ['@media (orientation:portrait)']: {
            backgroundImage: `url(${stepperBgPortrait})`,
            height: '10vh',
            padding: '80px',
          },
        }}
      >
        <Stepper
          activeStep={activeStep}
          orientation={matches ? 'vertical' : 'horizontal'}
          style={{ padding: 24, marginLeft: '80px' }}
          connector={
            <StepConnector
              sx={{
                '& .MuiStepConnector-line': {
                  minHeight: '100px',
                },
              }}
            />
          }
        >
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
              <StepContent
                sx={{
                  position: 'absolute',
                  left: '25%',
                  zIndex: 99,
                  top: '5%',
                  borderLeft: 0,
                  padding: 0,
                  margin: 0,
                  width: 'calc(75vw - 48px)',
                  maxWidth: 'calc(100vw - 48px)',
                  ['@media (orientation:portrait)']: {
                    left: '0',
                    top: '120px',
                    width: '100vw',
                    maxWidth: '100vw',
                  },
                }}
              >
                <Typography component="div">
                  {
                    <GetStepContent
                      enableAccNextButton={getAccNextBtnState}
                      enableEqNextButton={getEqNextBtnState}
                      enableHeadersNextButton={getHeadersNextBtnState}
                      activeStepParam={index}
                      tenantInfo={getTenantInfo}
                      selectedCrops={getSelectedCrops}
                      editTenant={getIsEditTenant}
                      enableNextButton={getEnableNextButton}
                    />
                  }
                </Typography>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Grid>
      <Box
        sx={{
          mx: 10,
          textAlign: 'end',
          zIndex: 99,
          position: 'absolute',
          bottom: '20px',
          right: '20px',

          ['@media (orientation:portrait)']: {
            '&&': {
              position: 'fixed',
              display: 'flex',
              flexDirection: 'row-reverse',
              margin: 0,
              padding: '20px',
              backgroundColor: colors.white,
              boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.3)',
              right: 0,
              bottom: 0,
              '& > div': {
                display: 'flex',
              },
            },
          },
        }}
      >
        <div>
          {showPermissions.skipWizard ? (
            <Button color="primary" onClick={handleSkipWizard}>
              {t('skip')}
            </Button>
          ) : null}
          <Button
            disabled={activeStep === 0}
            color="primary"
            onClick={handleBack}
          >
            {t('back')}
          </Button>
          <Button
            variant="contained"
            disabled={
              (activeStep === 0 && !accNextBtnState) ||
              (activeStep === 1 && !eqNextBtnState) ||
              (activeStep === 2 && !headersNextBtnState) ||
              (activeStep === 3 && !enableNextBtnState)
            }
            onClick={handleNext}
            sx={{ borderRadius: '25px', width: '160px' }}
            endIcon={<ArrowForwardIcon />}
          >
            {activeStep === steps.length - 1 ? t('finish') : t('next')}
          </Button>
        </div>
      </Box>
      <Grid
        size={{ md: 8, lg: 8 }}
        sx={{
          left: '20%',
          height: '100vh',
          borderRadius: '50px',
          position: 'absolute',
          background: 'white',
          display: 'flex',

          ['@media (orientation:landscape)']: {
            left: '20%',
            height: '100vh',
            borderRadius: '50px 0 0 50px',
            position: 'absolute',
            background: 'white',
            display: 'flex',
          },
          ['@media (orientation:portrait)']: {
            left: '0',
            top: '120px',
          },
        }}
        style={{ zIndex: 9 }}
      ></Grid>
    </Grid>
  )
}
